/**
 * army-product.js
 *
 * Image lookup uses 4 strategies in order:
 *  1. variant.image_url      — variant has a featured_image set in Shopify admin
 *  2. variant_ids            — image explicitly linked to variant in admin
 *  3. alt-text match         — image alt contains the selected colour name
 *  4. position-based         — colour index (0,1,2…) maps to image index
 */

(function () {
  'use strict';

  const data = window.ProductData;
  if (!data) return;

  /* ── DOM refs ─────────────────────────────────────────────── */
  const mainImg   = document.getElementById('ProductMainImage');
  const priceEl   = document.getElementById('ProductPrice');
  const compareEl = document.getElementById('ProductCompare');
  const availEl   = document.getElementById('ProductAvail');
  const variantIn = document.getElementById('ProductVariantId');
  const addBtn    = document.getElementById('ProductAddBtn');
  const addLabel  = document.getElementById('ProductAddLabel');
  const addForm   = document.getElementById('ProductAddForm');
  const thumbs    = document.querySelectorAll('.ac-product__thumb');
  const swatches  = document.querySelectorAll('.ac-swatch');
  const sizeBtns  = document.querySelectorAll('.ac-size-btn');
  const optLabels = document.querySelectorAll('.ac-product__option-selected');

  /* ── State ────────────────────────────────────────────────── */
  let selectedOptions = {};

  /* Detect the colour option name ("Colour" / "Color") */
  const colorOptName = data.options.find(o => /colou?r/i.test(o)) || null;

  const urlVariantId   = new URLSearchParams(window.location.search).get('variant');
  const initialVariant = (urlVariantId
    ? data.variants.find(v => v.id === parseInt(urlVariantId, 10))
    : null) || data.variants.find(v => String(v.id) === variantIn?.value) || data.variants[0];

  if (initialVariant) {
    data.options.forEach((name, i) => { selectedOptions[name] = initialVariant.options[i]; });
  }

  /* ── Helpers ─────────────────────────────────────────────── */
  function formatMoney(cents) {
    return (cents / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
  }

  function findVariant() {
    return data.variants.find(v =>
      data.options.every((name, i) => v.options[i] === selectedOptions[name])
    ) || null;
  }

  /**
   * Return the best image entry for the current variant.
   *
   * Strategy 1 — variant.image_url (admin has linked an image to the variant)
   * Strategy 2 — variant_ids match (image has this variant in its variants array)
   * Strategy 3 — image alt text contains the selected colour value
   * Strategy 4 — colour position index maps to image index
   *              (works when images are uploaded in the same order as colour options)
   */
  function imageForVariant(variant) {
    if (!variant || !data.images.length) return null;

    /* ── Strategy 1: featured_image set in admin ── */
    if (variant.image_url) {
      const exact = data.images.find(img => img.src === variant.image_url);
      return exact || {
        src: variant.image_url,
        zoom: variant.zoom_url || null,
        alt: variant.title,
        variant_ids: []
      };
    }

    /* ── Strategy 2: variant_ids ── */
    const byId = data.images.find(
      img => img.variant_ids && img.variant_ids.includes(variant.id)
    );
    if (byId) return byId;

    /* ── Strategy 3: alt text contains the colour value ── */
    if (colorOptName) {
      const colorVal = (selectedOptions[colorOptName] || '').toLowerCase().trim();
      if (colorVal) {
        const byAlt = data.images.find(
          img => img.alt && img.alt.toLowerCase().includes(colorVal)
        );
        if (byAlt) return byAlt;
      }

      /* ── Strategy 4: position-based ── */
      const colorValues = (data.option_values && data.option_values[colorOptName]) || [];
      const colorIdx = colorValues.findIndex(
        v => v.toLowerCase() === (selectedOptions[colorOptName] || '').toLowerCase()
      );
      if (colorIdx >= 0) {
        /* If images[0] is a lifestyle/hero shot (no variant in its variant_ids
           and there are more images than colour values), skip it. */
        const hasLifestyleFirst =
          data.images.length > colorValues.length &&
          data.images[0].variant_ids &&
          data.images[0].variant_ids.length === 0;

        const imgIdx = hasLifestyleFirst ? colorIdx + 1 : colorIdx;
        if (data.images[imgIdx]) return data.images[imgIdx];
      }

      /* ── Strategy 5: selected swatch index maps to thumbnail index ──
         Useful when admin variant-image links are missing but swatches and
         thumbnails are arranged in the same order. */
      const colorSwatches = Array.from(swatches).filter(
        sw => sw.dataset.option === colorOptName
      );
      const selectedColor = (selectedOptions[colorOptName] || '').toLowerCase().trim();
      const selectedSwatchIdx = colorSwatches.findIndex(
        sw => (sw.dataset.value || '').toLowerCase().trim() === selectedColor
      );
      if (selectedSwatchIdx >= 0 && thumbs[selectedSwatchIdx]) {
        return {
          src: thumbs[selectedSwatchIdx].dataset.src,
          zoom: thumbs[selectedSwatchIdx].dataset.zoomSrc || null,
          alt: selectedOptions[colorOptName] || variant.title,
          variant_ids: []
        };
      }
    }

    /* Last resort: first image */
    return data.images[0];
  }

  /* ── Render ──────────────────────────────────────────────── */
  /**
   * Swap the main photo. Accepts either:
   *  - an image-entry object  { src, alt }
   *  - a plain URL string
   */
  function zoomSrcFor(imgOrSrc) {
    if (imgOrSrc && typeof imgOrSrc === 'object' && imgOrSrc.zoom) return imgOrSrc.zoom;
    const src = (typeof imgOrSrc === 'string') ? imgOrSrc : (imgOrSrc && imgOrSrc.src);
    if (!src) return '';
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', '2400');
      return url.toString();
    } catch (err) {
      return src;
    }
  }

  function updateMainImage(imgOrSrc, fallbackAlt) {
    const src = (typeof imgOrSrc === 'string') ? imgOrSrc : (imgOrSrc && imgOrSrc.src);
    const alt = (typeof imgOrSrc === 'string') ? (fallbackAlt || '') : (imgOrSrc && imgOrSrc.alt) || '';

    if (!src || !mainImg) return;

    /* Must remove srcset/sizes or the browser ignores the src change */
    mainImg.removeAttribute('srcset');
    mainImg.removeAttribute('sizes');
    mainImg.src = src;
    mainImg.alt = alt;
    mainImg.dataset.zoomSrc = zoomSrcFor(imgOrSrc);

    /* Sync thumbnail highlight */
    thumbs.forEach(th => th.classList.toggle('is-active', th.dataset.src === src));
    if (typeof window.ArmyProductZoomSync === 'function') window.ArmyProductZoomSync();
  }

  function updateVariantUI(variant) {
    if (!variant) {
      if (addBtn)   addBtn.disabled      = true;
      if (addLabel) addLabel.textContent = 'Unavailable';
      return;
    }

    if (variantIn) variantIn.value = variant.id;

    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    if (compareEl) {
      const sale = variant.compare_at_price > variant.price;
      compareEl.textContent = sale ? formatMoney(variant.compare_at_price) : '';
      compareEl.hidden      = !sale;
    }

    if (availEl) {
      availEl.textContent = variant.available ? 'In stock' : 'Sold out';
      availEl.className   = 'ac-product__avail ' + (variant.available ? 'is-in-stock' : 'is-sold-out');
    }

    if (addBtn)   addBtn.disabled      = !variant.available;
    if (addLabel) addLabel.textContent = variant.available ? 'Add to Cart' : 'Sold Out';

    /* Shareable / back-button URL */
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({ variantId: variant.id }, '', url.toString());
  }

  function syncActiveStates() {
    /* Update "selected colour" inline label */
    optLabels.forEach(el => {
      const idx = parseInt(el.id.replace('OptionSelected-', ''), 10);
      if (!isNaN(idx) && data.options[idx]) {
        el.textContent = selectedOptions[data.options[idx]] || '';
      }
    });

    [...swatches, ...sizeBtns].forEach(control => {
      const selected = selectedOptions[control.dataset.option] === control.dataset.value;
      control.classList.toggle('is-selected', selected);
      const input = control.querySelector('input[type="radio"]');
      if (input) input.checked = selected;
    });
  }

  /* ── Init ────────────────────────────────────────────────── */
  function init() {
    const variant = findVariant();
    updateVariantUI(variant);
    syncActiveStates();
    updateMainImage(imageForVariant(variant));
  }

  /* ── Swatch clicks ───────────────────────────────────────── */
  swatches.forEach(sw => {
    sw.addEventListener('click', () => {
      selectedOptions[sw.dataset.option] = sw.dataset.value;
      const variant = findVariant();
      syncActiveStates();
      updateVariantUI(variant);
      updateMainImage(imageForVariant(variant));
    });
  });

  /* Also react to native radio changes (keyboard/touch edge cases). */
  document.querySelectorAll('.ac-swatch input[type="radio"]').forEach(input => {
    input.addEventListener('change', (e) => {
      const parent = e.currentTarget.closest('.ac-swatch');
      if (!parent) return;
      selectedOptions[parent.dataset.option] = parent.dataset.value;
      const variant = findVariant();
      syncActiveStates();
      updateVariantUI(variant);
      updateMainImage(imageForVariant(variant));
    });
  });

  /* ── Size clicks ─────────────────────────────────────────── */
  sizeBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      selectedOptions[btn.dataset.option] = btn.dataset.value;
      syncActiveStates();
      updateVariantUI(findVariant());
    });
  });

  /* ── Thumbnail clicks ────────────────────────────────────── */
  thumbs.forEach(th => {
    th.addEventListener('click', () => updateMainImage({
      src: th.dataset.src,
      zoom: th.dataset.zoomSrc || '',
      alt: th.querySelector('img') ? th.querySelector('img').alt : ''
    }));
  });

  /* ── AJAX add-to-cart ────────────────────────────────────── */
  if (addForm) {
    addForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const variantId = variantIn ? parseInt(variantIn.value, 10) : null;
      if (!variantId) return;

      if (addBtn)   { addBtn.disabled = true; addBtn.classList.add('is-loading'); }
      if (addLabel) addLabel.textContent = 'Adding…';

      try {
        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        if (!resp.ok) throw new Error('cart/add.js returned ' + resp.status);

        await resp.json();
        if (addLabel) addLabel.textContent = 'Added!';
        setTimeout(() => { if (addLabel) addLabel.textContent = 'Add to Cart'; }, 2000);
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (err) {
        if (addLabel) addLabel.textContent = 'Error — Try Again';
        console.error('[army-product] add-to-cart:', err);
      } finally {
        if (addBtn) { addBtn.disabled = false; addBtn.classList.remove('is-loading'); }
      }
    });
  }

  /* ── Cart badge refresh ──────────────────────────────────── */
  document.addEventListener('cart:updated', async () => {
    try {
      const cart = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(r => r.json());
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.hidden      = cart.item_count === 0;
      });
    } catch (_) {}
  });

  init();

  /* ── Magnifier ───────────────────────────────────────────── */
  const stage = document.getElementById('ProductMainImg');
  const frame = document.getElementById('ProductMainFrame');
  const lens = document.getElementById('ProductZoomLens');
  const lensImg = document.getElementById('ProductZoomLensImage');
  const zoomOpen = document.getElementById('ProductZoomOpen');
  const zoomDialog = document.getElementById('ProductZoomDialog');
  const zoomDialogImg = document.getElementById('ProductZoomImage');
  if (!mainImg || !stage || !frame || !lens || !lensImg) return;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const ZOOM = 2.6;
  let lensSize = 188;
  let moveRaf = 0;
  let lastPoint = null;

  function currentZoomSrc() {
    return (mainImg && mainImg.dataset.zoomSrc) || zoomSrcFor(mainImg && (mainImg.currentSrc || mainImg.src));
  }

  function syncLensSource() {
    if (!lensImg || !mainImg) return;
    const zoom = currentZoomSrc();
    if (zoom && lensImg.getAttribute('src') !== zoom) lensImg.src = zoom;
  }

  window.ArmyProductZoomSync = syncLensSource;
  syncLensSource();

  function hideLens() {
    if (stage) stage.classList.remove('is-zooming');
  }

  function placeLens(point) {
    if (!frame || !lens || !lensImg || !finePointer.matches) return;
    const rect = frame.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = point.clientX - rect.left;
    const y = point.clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
      hideLens();
      return;
    }
    lensSize = Math.round(Math.max(148, Math.min(210, rect.width * 0.42)));
    lens.style.width = lensSize + 'px';
    lens.style.height = lensSize + 'px';
    lensImg.style.width = (rect.width * ZOOM) + 'px';
    lensImg.style.height = (rect.height * ZOOM) + 'px';
    lens.style.transform = 'translate3d(' + (x - lensSize / 2) + 'px,' + (y - lensSize / 2) + 'px,0)';
    lensImg.style.transform = 'translate3d(' + (lensSize / 2 - x * ZOOM) + 'px,' + (lensSize / 2 - y * ZOOM) + 'px,0)';
    stage.classList.add('is-zooming');
  }

  function queueLens(event) {
    lastPoint = event;
    if (moveRaf) return;
    moveRaf = requestAnimationFrame(() => {
      moveRaf = 0;
      if (lastPoint) placeLens(lastPoint);
    });
  }

  function openZoomDialog() {
    if (!zoomDialog || !zoomDialogImg || !mainImg) return;
    const zoom = currentZoomSrc();
    if (!zoom) return;
    zoomDialogImg.src = zoom;
    zoomDialogImg.alt = mainImg.alt || 'Zoomed product image';
    if (typeof zoomDialog.showModal === 'function' && !zoomDialog.open) zoomDialog.showModal();
  }

  if (frame && finePointer.matches) {
    frame.addEventListener('mouseenter', (event) => {
      syncLensSource();
      queueLens(event);
    });
    frame.addEventListener('mousemove', queueLens);
    frame.addEventListener('mouseleave', hideLens);
    window.addEventListener('scroll', hideLens, { passive: true });
  }

  if (frame) {
    frame.addEventListener('click', (event) => {
      if (finePointer.matches) return;
      if (event.target.closest('#ProductZoomOpen')) return;
      openZoomDialog();
    });
  }

  if (zoomOpen) {
    zoomOpen.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      openZoomDialog();
    });
  }

  if (zoomDialog) {
    zoomDialog.addEventListener('click', (event) => {
      if (event.target === zoomDialog || event.target.closest('[data-zoom-close]')) zoomDialog.close();
    });
  }
})();
